import React, { useState, useCallback, useMemo } from 'react'
import moment from 'moment'
import cx from 'classnames'

import {
  yaxisFormatterNumber,
  yaxisFormatter,
  tooltipLabelFormatter,
  tooltipFormatter,
  tooltipFormatterNumber,
  tooltipLabelFormatterUnits,
  formatNumber,
  CHART_HEIGHT,
  YAXIS_WIDTH,
  COLORS,
} from '../helpers'

import GenericChart from '../components/GenericChart'
import ReferralTradingVolumeChart from '../components/ReferralTradingVolumeChart'
import ReferralDepositVolumeChart from '../components/ReferralDepositVolumeChart'
import ReferralTradingDiscountRebateChart from '../components/ReferralTradingDiscountRebateChart'
import ReferralDepositDiscountRebateChart from '../components/ReferralDepositDiscountRebateChart'
import Card from '../components/Card'

import {
  useLastSubgraphBlock,
  useLastBlock,
  useReferralsVolumeData,
  useReferralsDepositVolumeData,
  useReferrerRegisterData,
  useReferreeRegisterData,
  useReferralDiscountRebateData,
} from '../dataProvider'

const NOW = Math.floor(Date.now() / 1000)

function Referrals(props) {
  const [fromValue, setFromValue] = useState()
  const [toValue, setToValue] = useState()

  const { mode, chainName } = props

  const setDateRange = useCallback(
    (range) => {
      setFromValue(
        new Date(Date.now() - range * 1000).toISOString().slice(0, 10),
      )
      setToValue(undefined)
    },
    [setFromValue, setToValue],
  )
  const from = fromValue
    ? Math.floor(new Date(fromValue).valueOf() / 1000)
    : undefined

  const to = toValue ? Math.floor(new Date(toValue).valueOf() / 1000) : NOW

  const params = {
    from,
    to,
    chainName: chainName,
  }

  const [referralsTradingVolumeData, referralsTradingVolumeLoading] =
    useReferralsVolumeData(params)

  const [referralsDepositVolumeData, referralsDepositVolumeLoading] =
    useReferralsDepositVolumeData(params)

  const [referrerRegisterData, referrerRegisterLoading] =
    useReferrerRegisterData(params)

  const [referreeRegisterData, referreeRegisterLoading] =
    useReferreeRegisterData(params)

  const [referralDiscountRebateData, referralDiscountRebateLoading] =
    useReferralDiscountRebateData(params)

  const [totalReferralTradingVolume, totalReferralTradingVolumeDelta] =
    useMemo(() => {
      if (!referralsTradingVolumeData) {
        return []
      }
      const totalReferralTradingVolume =
        referralsTradingVolumeData[referralsTradingVolumeData.length - 1]
          ?.accumulativeVolume
      const prevTotalReferralTradingVolume =
        referralsTradingVolumeData[referralsTradingVolumeData.length - 2]
          ?.accumulativeVolume
      const totalReferralTradingVolumeDelta =
        totalReferralTradingVolume && prevTotalReferralTradingVolume
          ? totalReferralTradingVolume - prevTotalReferralTradingVolume
          : null

      return [totalReferralTradingVolume, totalReferralTradingVolumeDelta]
    }, [referralsTradingVolumeData])

  const [totalReferralDepositVolume, totalReferralDepositVolumeDelta] =
    useMemo(() => {
      if (!referralsDepositVolumeData) {
        return []
      }
      const totalReferralDepositVolume =
        referralsDepositVolumeData[referralsDepositVolumeData.length - 1]
          ?.accumulativeVolume
      const prevTotalReferralDepositVolume =
        referralsDepositVolumeData[referralsDepositVolumeData.length - 2]
          ?.accumulativeVolume
      const totalReferralDepositVolumeDelta =
        totalReferralDepositVolume && prevTotalReferralDepositVolume
          ? totalReferralDepositVolume - prevTotalReferralDepositVolume
          : null

      return [totalReferralDepositVolume, totalReferralDepositVolumeDelta]
    }, [referralsDepositVolumeData])

  const [referralTradingRebateData, referralTradingRebateDelta] =
    useMemo(() => {
      if (!referralDiscountRebateData) {
        return []
      }
      const referralTradingRebateData =
        referralDiscountRebateData[referralDiscountRebateData.length - 1]
          ?.tradingCumulative
      const prevReferralTradingRebateData =
        referralDiscountRebateData[referralDiscountRebateData.length - 2]
          ?.tradingCumulative
      const referralTradingRebateDelta =
        referralTradingRebateData && prevReferralTradingRebateData
          ? referralTradingRebateData - prevReferralTradingRebateData
          : null

      return [referralTradingRebateData, referralTradingRebateDelta]
    }, [referralDiscountRebateData])

  const [referralDepositRebateData, referralDepositRebateDelta] =
    useMemo(() => {
      if (!referralDiscountRebateData) {
        return []
      }
      const referralDepositRebateData =
        referralDiscountRebateData[referralDiscountRebateData.length - 1]
          ?.depositCumulative
      const prevReferralDepositRebateData =
        referralDiscountRebateData[referralDiscountRebateData.length - 2]
          ?.depositCumulative
      const referralDepositRebateDelta =
        referralDepositRebateData && prevReferralDepositRebateData
          ? referralDepositRebateData - prevReferralDepositRebateData
          : null

      return [referralDepositRebateData, referralDepositRebateDelta]
    }, [referralDiscountRebateData])

  const [totalReferrerRegisterData, totalReferrerRegisterDataDelta] =
    useMemo(() => {
      if (!referrerRegisterData) {
        return []
      }
      const totalReferrerRegisterData =
        referrerRegisterData[referrerRegisterData.length - 1]?.cumulative
      const prevTotalReferrerRegisterData =
        referrerRegisterData[referrerRegisterData.length - 2]?.cumulative
      const totalReferrerRegisterDataDelta =
        totalReferrerRegisterData && prevTotalReferrerRegisterData
          ? totalReferrerRegisterData - prevTotalReferrerRegisterData
          : null

      return [totalReferrerRegisterData, totalReferrerRegisterDataDelta]
    }, [referrerRegisterData])

  const [totalReferreeRegisterData, totalReferreeRegisterDataDelta] =
    useMemo(() => {
      if (!referreeRegisterData) {
        return []
      }
      const totalReferreeRegisterData =
        referreeRegisterData[referreeRegisterData.length - 1]?.cumulative
      const prevTotalReferreeRegisterData =
        referreeRegisterData[referreeRegisterData.length - 2]?.cumulative
      const totalReferreeRegisterDataDelta =
        totalReferreeRegisterData && prevTotalReferreeRegisterData
          ? totalReferreeRegisterData - prevTotalReferreeRegisterData
          : null

      return [totalReferreeRegisterData, totalReferreeRegisterDataDelta]
    }, [referreeRegisterData])

  const [lastSubgraphBlock] = useLastSubgraphBlock()
  const [lastBlock] = useLastBlock()

  const isObsolete =
    lastSubgraphBlock &&
    lastBlock &&
    lastBlock.timestamp - lastSubgraphBlock.timestamp > 3600

  return (
    <div className="Home">
      <div className="page-title-section">
        <div className="page-title-block">
          <h1>PERP88 Referrals</h1>
          {lastSubgraphBlock && lastBlock && (
            <p className={cx('page-description', { warning: isObsolete })}>
              {isObsolete && 'Data is obsolete. '}
              Updated {moment(lastSubgraphBlock.timestamp * 1000).fromNow()}
              &nbsp;at block{' '}
              <a
                target="_blank"
                href={`https://polygonscan.com/block/${lastSubgraphBlock.number}`}
                rel="noreferrer"
              >
                {lastSubgraphBlock.number}
              </a>
            </p>
          )}
        </div>
      </div>

      <div className="my-3 grid gap-3 md:my-10 md:grid-cols-3 md:gap-6 ">
        <Card
          loading={
            !!!totalReferralTradingVolume || !!!totalReferralTradingVolumeDelta
          }
          title="Trading Volume"
          value={totalReferralTradingVolume}
          rawChanged24Hrs={totalReferralTradingVolumeDelta}
          displayChanged24Hrs={
            referralsTradingVolumeData &&
            formatNumber(totalReferralTradingVolumeDelta, {
              currency: true,
            })
          }
        />
        <Card
          loading={
            !!!referralsDepositVolumeData || !!!referralsDepositVolumeData
          }
          title="Deposit Volume"
          value={totalReferralDepositVolume}
          rawChanged24Hrs={totalReferralDepositVolumeDelta}
          displayChanged24Hrs={
            referralsDepositVolumeData &&
            formatNumber(totalReferralDepositVolumeDelta, {
              currency: true,
            })
          }
        />
        <Card
          loading={
            !!!referralTradingRebateData || !!!referralDiscountRebateData
          }
          title="Trading Rebates & Discounts"
          value={referralTradingRebateData}
          rawChanged24Hrs={referralTradingRebateDelta}
          displayChanged24Hrs={
            referralDiscountRebateData &&
            formatNumber(referralTradingRebateDelta, {
              currency: true,
            })
          }
        />
        <Card
          loading={
            !!!referralDepositRebateData || !!!referralDiscountRebateData
          }
          title="Depositing Rebates & Discounts"
          value={referralDepositRebateData}
          rawChanged24Hrs={referralDepositRebateDelta}
          displayChanged24Hrs={
            referralDiscountRebateData &&
            formatNumber(referralDepositRebateDelta, {
              currency: true,
            })
          }
        />
        <Card
          loading={
            !!!totalReferrerRegisterData || !!!referralsTradingVolumeData
          }
          title="Affiliates Registered"
          value={totalReferrerRegisterData}
          rawChanged24Hrs={totalReferrerRegisterDataDelta}
          displayChanged24Hrs={
            !isNaN(parseFloat(totalReferrerRegisterDataDelta)) &&
            totalReferrerRegisterDataDelta.toFixed(0)
          }
          isCurrency={false}
        />
        <Card
          loading={!!!totalReferreeRegisterData || !!!referreeRegisterData}
          title="Referrals Registrations"
          value={totalReferreeRegisterData}
          rawChanged24Hrs={totalReferreeRegisterDataDelta}
          displayChanged24Hrs={
            !isNaN(parseFloat(totalReferreeRegisterDataDelta)) &&
            totalReferreeRegisterDataDelta.toFixed(0)
          }
          isCurrency={false}
        />
      </div>

      <div className="chart-grid">
        <div className="chart-cell">
          <ReferralTradingVolumeChart
            data={referralsTradingVolumeData}
            loading={referralsTradingVolumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            yaxisTickFormatter={yaxisFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipFormatter={tooltipFormatter}
          />
        </div>
        <div className="chart-cell">
          <ReferralDepositVolumeChart
            data={referralsDepositVolumeData}
            loading={referralsDepositVolumeLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            yaxisTickFormatter={yaxisFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipFormatter={tooltipFormatter}
          />
        </div>
        <div className="chart-cell">
          <ReferralTradingDiscountRebateChart
            data={referralDiscountRebateData}
            loading={referralDiscountRebateLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            yaxisTickFormatter={yaxisFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipFormatter={tooltipFormatter}
          />
        </div>
        <div className="chart-cell">
          <ReferralDepositDiscountRebateChart
            data={referralDiscountRebateData}
            loading={referralDiscountRebateLoading}
            chartHeight={CHART_HEIGHT}
            yaxisWidth={YAXIS_WIDTH}
            xaxisTickFormatter={tooltipLabelFormatter}
            yaxisTickFormatter={yaxisFormatter}
            tooltipLabelFormatter={tooltipLabelFormatter}
            tooltipFormatter={tooltipFormatter}
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            syncId="syncC"
            loading={referrerRegisterLoading}
            title="Registered Affiliates"
            data={referrerRegisterData}
            yaxisDataKey="total"
            rightYaxisDataKey="cumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'total', type: 'Bar', name: 'Daily' },
              {
                key: 'cumulative',
                strokeWidth: 2,
                yAxisId: 'right',
                type: 'Line',
                name: 'Cumulative',
                color: COLORS[4],
              },
            ]}
            type="Composed"
          />
        </div>
        <div className="chart-cell">
          <GenericChart
            syncId="syncD"
            loading={referreeRegisterLoading}
            title="Referrals Registrations"
            data={referreeRegisterData}
            yaxisDataKey="total"
            rightYaxisDataKey="cumulative"
            yaxisTickFormatter={yaxisFormatterNumber}
            tooltipFormatter={tooltipFormatterNumber}
            tooltipLabelFormatter={tooltipLabelFormatterUnits}
            items={[
              { key: 'total', type: 'Bar', name: 'Daily', color: COLORS[2] },

              {
                key: 'cumulative',
                strokeWidth: 2,
                yAxisId: 'right',
                type: 'Line',
                name: 'Cumulative',
                color: COLORS[4],
              },
            ]}
            type="Composed"
            description="Traders registrations with referral code"
          />
        </div>
      </div>
    </div>
  )
}

export default Referrals
